// ObjectsList.js
import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Box,
  IconButton,
  Collapse
} from '@mui/material';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import ManualObjectEntry from './ManualObjectEntry';

const ObjectsList = ({ objects = [], serviceType, onObjectsChange }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const handleAddObject = (newObject) => {
    onObjectsChange([...objects, newObject]);
  };

  const handleDeleteObject = (index) => {
    const newObjects = objects.filter((_, i) => i !== index);
    onObjectsChange(newObjects);
  };

  const toggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const PropertyTable = ({ properties, level = 0 }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: level === 0 ? 0 : 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Required</TableCell>
            <TableCell>Array</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {properties.map((prop, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>
                  <Box sx={{ ml: level * 2, display: 'flex', alignItems: 'center' }}>
                    {(prop.type === 'object' || prop.type === 'array') && prop.properties?.length > 0 && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(`${level}-${index}`);
                        }}
                      >
                        {expandedRow === `${level}-${index}` ? 
                          <ChevronDown size={16} /> : 
                          <ChevronRight size={16} />
                        }
                      </IconButton>
                    )}
                    <span>{prop.name}</span>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={prop.type} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={prop.required ? 'Required' : 'Optional'}
                    size="small"
                    color={prop.required ? 'error' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {prop.isArray && (
                    <Chip 
                      label="Array"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>{prop.description}</TableCell>
              </TableRow>
              {(prop.type === 'object' || prop.type === 'array') && prop.properties?.length > 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 0, borderBottom: 'none' }}>
                    <Collapse in={expandedRow === `${level}-${index}`} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1 }}>
                        <PropertyTable properties={prop.properties} level={level + 1} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const ObjectDetails = ({ object }) => (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Description: {object.description || 'No description provided'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {object.isRequest && (
          <Chip 
            label="Request Object" 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        )}
        {object.isResponse && (
          <Chip 
            label="Response Object" 
            size="small" 
            color="success" 
            variant="outlined"
          />
        )}
      </Box>

      {object.properties?.length > 0 ? (
        <PropertyTable properties={object.properties} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No properties defined
        </Typography>
      )}
    </Box>
  );

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <ManualObjectEntry onAddObject={handleAddObject} />
      </Box>
      
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Properties</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {objects.map((object, index) => (
              <React.Fragment key={index}>
                <TableRow 
                  hover
                  onClick={() => toggleRow(index)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <TableCell padding="checkbox">
                    <IconButton size="small">
                      {expandedRow === index ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{object.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={object.type} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {object.isRequest && (
                        <Chip 
                          label="Request" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                      {object.isResponse && (
                        <Chip 
                          label="Response" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {object.properties?.length || 0} property(ies)
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteObject(index);
                      }}
                      color="error"
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                    <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                      <ObjectDetails object={object} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {objects.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No objects defined yet. Use the "Add Object" button to add objects manually, 
                    or use the service discovery feature.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ObjectsList;